import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import moment from "moment";
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import Button from "components/atoms/Button";
import InputDate from "components/atoms/InputDate";
import InputSelect from "components/atoms/InputSelect";
import TableDataComponent from "components/organisms/TableData";
import { formatParasite } from "utils/parasite";
import ActionButton from "components/atoms/ActionButton";

import RegmalFirst from "services/RegmalFirst";
import { getCurrentUserFromLocalStorage } from "utils/common";
import { MonthOptions, SampleSendTypeOptions, UserLevel } from "utils/constant";
import SurveilansFaskes from "services/SurveilansFaskes";
import SurveilansLab34 from "services/SurveilansKnowlesiLab34";
import SurveilansLab5 from "services/SurveilansKnowlesiLab5";
import Fasyankes from "services/Fasyankes";
import ModalSendLab from "./ModalSendLab";
import { sampleSendDates } from "./sampleSendRules";
import { buildSearchParams } from "./searchParams";
import { toast } from "react-toastify";
import { Check, CheckCircle, CheckSquare, Edit3 } from "react-feather";
import Lab from "services/User";
import Wilayah from "services/Wilayah";

const RegmalService = new RegmalFirst();
const SurveilansFaskesService = new SurveilansFaskes();
const SurveilansLab34Service = new SurveilansLab34();
const SurveilansLab5Service = new SurveilansLab5();
const LabService = new Lab();
const WilayahService = new Wilayah();
const FasyankesService = new Fasyankes();

const InputSurveilansKnowlesi = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // State saja tidak cukup: klik kedua bisa masuk sebelum render ulang.
  const savingRef = useRef(false);

  const [regmalState, setRegmalState] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });

  const [filterState, setFilterState] = useState({
    filter_year: "",
    filter_month: "",
    filter_sample_type: "",
    filter_fasyankes: "",
  });

  // Opsi fasyankes kabkota user; hanya diisi untuk login kabkota.
  const [fasyankesOptions, setFasyankesOptions] = useState([]);

  const [labData, setLabData] = useState([]);
  const [editedRows, setEditedRows] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalRow, setModalRow] = useState(null);
  const [modalInitial, setModalInitial] = useState(null);

  const currentUser = getCurrentUserFromLocalStorage();

  const getRowKey = useCallback(
    (row) => row.regmal_id ?? row.id ?? row.slide_code,
    []
  );

  // Map id->label untuk lookup cepat
  const labLabelById = useMemo(() => {
    const map = {};
    for (const opt of labData) {
      map[String(opt.value)] = opt.label;
    }
    return map;
  }, [labData]);

  const getAllRegmalKnowlesi = async () => {
    const { searchBy, searchValue } = buildSearchParams(filterState);

    setIsLoading(true);
    try {
      const response = await SurveilansFaskesService.getAll({
        page: regmalState.page,
        limit: regmalState.limit,
        searchBy,
        searchValue,
      });

      setRegmalState((prevState) => ({
        ...prevState,
        data: response.surveilans_malaria_fasyankes || [],
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));

      setEditedRows({});
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const mapLevelToLabel = (level) => {
    const levelMap = {
      [UserLevel.LabTier2]: "Lab Tingkat 2",
      [UserLevel.LabTier3]: "Lab Tingkat 3",
      [UserLevel.LabTier4]: "Lab Tingkat 4",
      [UserLevel.LabTier5]: "Lab Tingkat 5",
    };
    return levelMap[level] || level;
  };

  // Lab rujukan nasional, tampil untuk semua provinsi
  const labTier5Option = {
    label:
      "Lab Tingkat 5: Balai Besar Laboratorium Biologi Kesehatan (1001214268)",
    value: "1001214268|Balai Besar Laboratorium Biologi Kesehatan",
    level: UserLevel.LabTier5,
  };

  const buildLabOptions = (users) =>
    users?.map((item) => ({
      label: mapLevelToLabel(item.level) + " - " + item?.first_name,
      value: item?.username + "|" + item?.first_name,
      level: item.level,
    })) ?? [];

  const fetchLabByProvince = async (provinceIdRaw) => {
    // currentUser.province_id berbentuk "11|ACEH", ambil id-nya saja
    const provinceId = String(provinceIdRaw).split("|")[0];

    try {
      // Lab tingkat 3 tetap mengikuti provinsi user
      const tier3Res = await LabService.getAll({
        page: 1,
        limit: 9999,
        searchBy: "province_id,level",
        searchValue: `${provinceId},${UserLevel.LabTier3}`,
      });

      // Lab tingkat 4 diambil per region (gabungan provinsi se-region)
      let regionId;
      try {
        const regionRes = await WilayahService.getProvinceRegion(provinceId);
        regionId = regionRes?.region_id;
      } catch (error) {
        console.log(error);
      }

      // Fallback ke provinsi bila region belum terpetakan
      const tier4Params = regionId
        ? {
            page: 1,
            limit: 9999,
            searchBy: "region_id,level",
            searchValue: `${regionId},${UserLevel.LabTier4}`,
          }
        : {
            page: 1,
            limit: 9999,
            searchBy: "province_id,level",
            searchValue: `${provinceId},${UserLevel.LabTier4}`,
          };
      const tier4Res = await LabService.getAll(tier4Params);

      setLabData([
        ...buildLabOptions(tier3Res.users),
        ...buildLabOptions(tier4Res.users),
        labTier5Option,
      ]);
    } catch (error) {
      console.log(error);
    }
  };

  // Fasyankes milik kabkota user, dipakai filter "Pilih Fasyankes".
  const fetchFasyankesByDistrict = async (districtIdRaw) => {
    // currentUser.district_id berbentuk "1108|KAB. X", backend butuh id-nya saja
    const districtId = String(districtIdRaw).split("|")[0];

    try {
      const response = await FasyankesService.getAll("fasyankes", {
        page: 1,
        limit: 999,
        searchBy: "district_id",
        searchValue: districtId,
      });

      setFasyankesOptions(
        (response?.fasyankes || []).map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    } catch (error) {
      // Gagal ambil opsi: dropdown kosong, filter lain dan tombol Terapkan
      // tetap jalan (filter fasyankes hanya opsional).
      console.log(error);
      setFasyankesOptions([]);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (currentUser?.province_id) {
      fetchLabByProvince(currentUser.province_id);
    }
  }, [currentUser?.province_id]);

  // Login kabkota saja yang punya filter fasyankes (backend tidak menyediakan
  // parameter ini untuk level lain).
  useEffect(() => {
    if (currentUser?.level === UserLevel.District && currentUser?.district_id) {
      fetchFasyankesByDistrict(currentUser.district_id);
    }
  }, [currentUser?.level, currentUser?.district_id]);

  useEffect(() => {
    getAllRegmalKnowlesi();
  }, [regmalState.page]);

  const onPageChange = (page) => {
    setRegmalState((prevState) => ({
      ...prevState,
      page,
    }));
  };

  const openModal = (rowOriginal, isEdit = false) => {
    setModalRow({ ...rowOriginal, isEdit });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalRow(null);
    setModalInitial(null);
  };

  const submitModal = async (values) => {
    // Klik Simpan berulang saat jaringan lambat tidak boleh memulai rantai simpan kedua.
    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);

    const faskesPayload = {
      regmal_id: values.regmal_id,
      destination_lab_id: values.destination_lab_id || null,
      ...sampleSendDates(values),
      sample_send_type: values.sample_send_type || [],
    };

    const labPayload = {
      regmal_id: values.regmal_id,
      // Fasyankes asal kasus (bukan fasyankes user), agar laporan lab tetap benar.
      source_fasyankes_id:
        values.fasyankes_origin_id || modalRow?.fasyankes_origin_id,
      destination_lab_id: values.destination_lab_id,
    };

    const isLabTier5 = values.destination_lab_id === labTier5Option.value;

    try {
      if (values.smf_id) {
        await SurveilansFaskesService.update(faskesPayload, values.smf_id);
      } else {
        await SurveilansFaskesService.add(faskesPayload);
      }

      if (isLabTier5) {
        // Pindah tujuan dari lab tingkat 3/4 ke lab tingkat 5.
        // Simpan tujuan baru dulu; relasi lama baru dihapus setelah sukses.
        const lab5Payload = {
          ...labPayload,
          sample_send_date: values.sample_send_date || null,
        };
        if (values.skl5_id) {
          await SurveilansLab5Service.update(lab5Payload, values.skl5_id);
        } else {
          await SurveilansLab5Service.add(lab5Payload);
        }
        if (values.skl34_id) {
          await SurveilansLab34Service.remove(values.skl34_id);
        }
      } else {
        // Pindah tujuan dari lab tingkat 5 ke lab tingkat 3/4.
        if (values.skl34_id) {
          await SurveilansLab34Service.update(labPayload, values.skl34_id);
        } else {
          await SurveilansLab34Service.add(labPayload);
        }
        if (values.skl5_id) {
          await SurveilansLab5Service.remove(values.skl5_id);
        }
      }

      await getAllRegmalKnowlesi();
      toast.success("Data berhasil disimpan");
      // Modal ditutup oleh ModalSendLab setelah seluruh tahap selesai.
      return true;
    } catch (err) {
      console.error("Gagal simpan data", err);
      toast.error("Gagal simpan data");
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // helper untuk baca "nilai yang tampil" = draft || original
  const getEffectiveField = useCallback(
    (row, field) => {
      const key = getRowKey(row);
      const draft = editedRows[key];
      if (draft && draft[field] !== undefined && draft[field] !== null) {
        return draft[field];
      }
      return row[field];
    },
    [editedRows, getRowKey]
  );

  const columns = useMemo(
    () => [
      {
        header: "Aksi",
        accessorKey: "action",
        cell: ({ row }) => {
          const isDikirim = row.original.status_dikirim_lab;
          const bisaDiedit = row.original.bisa_diedit;

          if (isDikirim && bisaDiedit) {
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minHeight: 0 }}>
                <ActionButton
                  onClick={() => openModal(row.original, true)}
                  btnType={"edit"}
                />
                <div
                  style={{
                    color: "#198754",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <CheckSquare size={18} className="mr-2" />
                  Terkirim
                </div>
              </div>
            );
          } else if (isDikirim && !bisaDiedit) {
            return (
              <div
                style={{
                  color: "#198754",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <CheckSquare size={18} className="mr-2" />
                Terkirim
              </div>
            );
          } else {
            return (
              <ActionButton
                onClick={() => openModal(row.original)}
                btnType={"kirim"}
              />
            );
          }
        },
      },
      { header: "Nama Pasien", accessorKey: "name" },
      { header: "Fasyankes", accessorKey: "fasyankes_name" },
      { header: "Kode Slide", accessorKey: "slide_code" },
      {
        header: "Desa Domisili",
        accessorKey: "urbanvillage",
        cell: ({ row }) => (
          <span
            className="cell-ellipsis"
            title={row.original.urbanvillage_id || "-"}
          >
            {row.original.urbanvillage_id || "-"}
          </span>
        ),
      },
      {
        header: "Alamat Domisili",
        accessorKey: "address",
        cell: ({ row }) => (
          <span className="cell-ellipsis" title={row.original.address || "-"}>
            {row.original.address || "-"}
          </span>
        ),
      },
      {
        header: "Jenis Parasit",
        accessorKey: "parasyte",
        cell: ({ getValue }) => formatParasite(getValue()),
      },
      {
        header: "Pekerjaan",
        accessorKey: "job",
        cell: ({ row }) => (
          <span className="cell-ellipsis" title={row.original.job || "-"}>
            {row.original.job || "-"}
          </span>
        ),
      },
      {
        header: "Jenis Sampel yang Dikirim",
        accessorKey: "sample_send_type",
        size: 220,
        minSize: 180,
        cell: ({ row }) => (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {row.original.sample_send_type &&
              row.original.sample_send_type.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
          </ul>
        ),
      },
      {
        header: "Kirim Sampel ke Lab rujukan Pemeriksaan PCR (Tingkat 3 / Tingkat 4/ Tingkat 5)",
        accessorKey: "destination_lab_id",
        cell: ({ row }) => {
          const draft = editedRows[getRowKey(row.original)];
          const id = getEffectiveField(row.original, "destination_lab_id");
          const label = id ? labLabelById[String(id)] || `#${id}` : "-";

          return (
            <div className="cell-ellipsis" title={label}>
              {label}
              {draft && (
                <span className="badge-draft" style={{ marginLeft: 8 }}>
                  Draft
                </span>
              )}
            </div>
          );
        },
      },
      {
        header: "Tanggal pengiriman sampel",
        accessorKey: "sample_send_date",
        cell: ({ row }) => {
          const draft = editedRows[getRowKey(row.original)];
          // Baris lama bisa hanya punya tanggal DBS/WB, jadi pakai fallback berurutan.
          const raw =
            getEffectiveField(row.original, "sample_sad_send_date") ||
            getEffectiveField(row.original, "sample_dbs_send_date") ||
            getEffectiveField(row.original, "sample_wb_send_date");
          const display = raw ? moment(raw).format("YYYY-MM-DD") : "-";
          return (
            <div className="cell-ellipsis" title={display}>
              {display}
              {draft && (
                <span className="badge-draft" style={{ marginLeft: 8 }}>
                  Draft
                </span>
              )}
            </div>
          );
        },
      },
    ],
    [editedRows, getEffectiveField, getRowKey, labLabelById]
  );

  const tableInstance = useReactTable({
    columns,
    data: regmalState?.data || [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="dashboard-wrapper surveilans-knowlesi-page">
      <style>{`
        .cell-ellipsis{display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .cell-popover{position:relative;overflow:visible}
        .rdtPicker{z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,.12);border-radius:8px}
        .badge-draft{display:inline-block;font-size:11px;line-height:1;padding:3px 6px;border-radius:4px;background:#FFF3CD;color:#B45309;border:1px solid #FDE68A}
        /* Tinggi tabel ikut konten agar filter & tabel scroll bersama di container
           (lawan inline flex:1/min-height:0 dari TableDataComponent). */
        .surveilans-knowlesi-page > .table-wrapper{flex:none !important;min-height:0 !important}
      `}</style>

      <h1 className="page-title">
        Input Surveilans {formatParasite("Knowlesi")}
      </h1>

      {/* Informasi wilayah */}
      <div className="informasi-wilayah">
        <section className="list-item">
          <p className="item-label">Provinsi</p>
          <p className="item-value">
            {currentUser?.province_id?.split("|")[1]}
          </p>
        </section>
        <hr />
        <section className="list-item">
          <p className="item-label">Kab/Kota</p>
          <p className="item-value">
            {currentUser?.district_id?.split("|")[1]}
          </p>
        </section>
      </div>

      {/* Filter + Aksi di atas tabel */}
      <div
        className="card-filter"
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div className="filter-item">
          <InputDate
            placeholder={"Pilih Tahun"}
            name={"filter_year"}
            dateFormat={"YYYY"}
            onChange={(e) => {
              const selectedYear = e ? moment(e).format("YYYY") : "";
              setFilterState((s) => ({ ...s, filter_year: selectedYear }));
            }}
            value={filterState.filter_year}
            isClearable
          />
        </div>

        <div className="filter-item">
          <InputSelect
            placeholder={"Pilih Bulan"}
            data={MonthOptions}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              setFilterState((s) => ({ ...s, filter_month: selectedValue }));
            }}
            value={MonthOptions.find(
              (item) => item.value === filterState.filter_month
            )}
          />
        </div>

        <div className="filter-item">
          <InputSelect
            placeholder={"Pilih Jenis Sampel"}
            data={SampleSendTypeOptions}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              setFilterState((s) => ({
                ...s,
                filter_sample_type: selectedValue,
              }));
            }}
            value={SampleSendTypeOptions.find(
              (item) => item.value === filterState.filter_sample_type
            )}
          />
        </div>

        {/* Filter fasyankes hanya untuk login kabkota; role lain tidak melihat
            item ini dan tidak mengirim parameter fasyankes. */}
        {currentUser?.level === UserLevel.District && (
          <div className="filter-item">
            <InputSelect
              placeholder={"Pilih Fasyankes"}
              data={fasyankesOptions}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                setFilterState((s) => ({
                  ...s,
                  filter_fasyankes: selectedValue,
                }));
              }}
              value={fasyankesOptions.find(
                (item) => item.value === filterState.filter_fasyankes
              )}
            />
          </div>
        )}

        <Button
          variant={"primary"}
          size={"normal"}
          onClick={() => getAllRegmalKnowlesi()}
        >
          Terapkan
        </Button>
      </div>

      <TableDataComponent
        columnLength={columns.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={isLoading}
        currentPage={regmalState?.currentPage}
        limit={regmalState?.limit}
        nextPage={regmalState?.nextPage}
        previousPage={regmalState?.previousPage}
        total={regmalState?.totalData}
      />

      <ModalSendLab
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={submitModal}
        labOptions={labData}
        initialData={modalInitial}
        modalRow={modalRow}
        isSaving={saving}
        requireConfirmation={currentUser?.level === UserLevel.District}
      />
    </div>
  );
};

export default InputSurveilansKnowlesi;
